import { TStore } from '@components/types';
import { Button, Select, Switch } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import React, { useEffect, useState } from 'react'
const { Option } = Select;

type Props = {
    store: TStore
    orderInstructions: any,
    setOrderInstructions: Function
}

const DeliverySlots = ({ store, orderInstructions, setOrderInstructions }: Props) => {
    const [currentWeekStart, setCurrentWeekStart] = useState(0); // Week offset from today
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedHour, setSelectedHour] = useState('06');
    const [selectedMinute, setSelectedMinute] = useState('30');
    const [selectedPeriod, setSelectedPeriod] = useState('PM');
    const [isDeliveryDate, setIsDeliveryDate] = useState(false)
    const [selectedTime, setSelectedTime] = useState('')

    // Validate and adjust time selection when today is selected
    const validateTimeSelection = () => {
        if (selectedDate.isSame(moment(), 'day')) {
            const minTime = moment().add(1, 'hour');
            const selectedTime = moment()
                .hour(selectedPeriod === 'PM' && selectedHour !== '12' ? parseInt(selectedHour) + 12 :
                    selectedPeriod === 'AM' && selectedHour === '12' ? 0 : parseInt(selectedHour))
                .minute(parseInt(selectedMinute));

            // If selected time is before minimum time, adjust it
            if (selectedTime.isBefore(minTime)) {
                setSelectedHour(minTime.format('hh'));
                setSelectedMinute(getClosestMinute(minTime.minute()));
                setSelectedPeriod(minTime.format('A'));
            }
        }
    };

    // Validate time when hour, minute, or period changes
    useEffect(() => {
        if (store?.storemeta?.settings?.schedule_delivery && store?.storemeta?.settings?.delivery_slots?.length === 0) {
            validateTimeSelection();
        }
    }, [selectedHour, selectedMinute, selectedPeriod, selectedDate]);

    // Update orderInstructions when date/time changes
    useEffect(() => {
        if (store?.storemeta?.settings?.schedule_delivery && store?.storemeta?.settings?.delivery_slots?.length === 0) {
            const deliveryDateTime = moment(selectedDate)
                .hour(selectedPeriod === 'PM' && selectedHour !== '12' ? parseInt(selectedHour) + 12 :
                    selectedPeriod === 'AM' && selectedHour === '12' ? 0 : parseInt(selectedHour))
                .minute(parseInt(selectedMinute));

            setOrderInstructions({
                ...orderInstructions,
                delivery_date: isDeliveryDate ? deliveryDateTime?.format('YYYY-MM-DDTHH:mm:ss') : null
            });
        }
    }, [selectedDate, selectedHour, selectedMinute, selectedPeriod]);

    const getClosestMinute = (currentMinute: number) => {
        const minutes = ['00', '15', '30', '45'];
        const closest = minutes?.reduce((prev, curr) =>
            Math?.abs(parseInt(curr) - currentMinute) < Math?.abs(parseInt(prev) - currentMinute) ? curr : prev
        );
        return closest;
    };

    // Generate 7 days starting from currentWeekStart
    const generateDates = () => {
        const dates = [];
        const startDate = moment()?.add(currentWeekStart, 'days');

        for (let i = 0; i < 7; i++) {
            const date = moment(startDate)?.add(i, 'days');
            // Only include dates within 15 days from today and not in the past
            if (date?.isSameOrAfter(moment(), 'day') && date?.isBefore(moment()?.add(15, 'days'))) {
                dates.push(date);
            }
        }
        return dates;
    };

    const dates = generateDates();

    // Generate available hours based on selected date
    const getAvailableHours = () => {
        const allHours = Array.from({ length: 12 }, (_, i) => {
            const hour = (i + 1)?.toString()?.padStart(2, '0');
            return hour;
        });

        // If today is selected, filter hours based on current time + 1 hour
        if (selectedDate?.isSame(moment(), 'day')) {
            const minTime = moment().add(1, 'hour');
            const minHour12 = minTime.format('hh');
            const minPeriod = minTime.format('A');

            return allHours.filter(hour => {
                if (selectedPeriod !== minPeriod) {
                    return selectedPeriod === 'PM' && minPeriod === 'AM';
                }
                return parseInt(hour) >= parseInt(minHour12);
            });
        }

        return allHours;
    };

    // Generate available minutes based on selected date and hour
    const getAvailableMinutes = () => {
        const allMinutes = ['00', '15', '30', '45'];

        // If today is selected, filter minutes based on current time + 1 hour
        if (selectedDate?.isSame(moment(), 'day')) {
            const minTime = moment().add(1, 'hour');
            const minHour12 = minTime?.format('hh');
            const minMinute = minTime?.minute();
            const minPeriod = minTime?.format('A');

            // If same period and same hour, filter minutes
            if (selectedPeriod === minPeriod && selectedHour === minHour12) {
                const closestMinute = getClosestMinute(minMinute);
                const minMinuteIndex = allMinutes.indexOf(closestMinute);
                return allMinutes.slice(minMinuteIndex);
            }
        }

        return allMinutes;
    };

    const availableHours = getAvailableHours();
    const availableMinutes = getAvailableMinutes();

    const handlePrevWeek = () => {
        // Don't go before today
        if (currentWeekStart > 0) {
            setCurrentWeekStart(currentWeekStart - 7);
        }
    };

    const handleNextWeek = () => {
        // Don't go beyond 15 days
        if (currentWeekStart + 7 < 15) {
            setCurrentWeekStart(currentWeekStart + 7);
        }
    };

    const handleTimeSelect = (time: string) => {
        if (time) {
            setSelectedTime(time);
            setOrderInstructions({
                ...orderInstructions,
                delivery_date: moment(selectedDate)?.format('YYYY-MM-DD ' + time),

            })
        }
    };

    const handleDateSelect = (date: any) => {
        setSelectedDate(date);
        // If today is selected, automatically set time to current + 1 hour
        if (date?.isSame(moment(), 'day')) {
            const futureTime = moment().add(1, 'hour');
            setSelectedHour(futureTime?.format('hh'));
            setSelectedMinute(getClosestMinute(futureTime?.minute()));
            setSelectedPeriod(futureTime?.format('A'));
        }
    };

    const canGoPrev = currentWeekStart > 0;
    const canGoNext = currentWeekStart + 7 < 15;

    const timeSelection = () => {
        return (
            <div className="flex gap-4 items-end py-4 justify-center">
                <div className="flex flex-col">
                    <label className="text-xs text-neutral-600 font-semibold mb-1 text-center">Hour</label>
                    <Select
                        value={selectedHour}
                        onChange={setSelectedHour}
                        className="w-14 text-center"
                        size="small"
                        suffixIcon={null}
                    >
                        {availableHours.map((hour) => (
                            <Option key={hour} value={hour}>
                                <span className='text-center'>{hour}</span>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className="flex flex-col">
                    <label className="text-xs text-neutral-600 font-semibold mb-1 text-center">Minute</label>
                    <Select
                        value={selectedMinute}
                        onChange={setSelectedMinute}
                        className="w-14 text-center"
                        size="small"
                        suffixIcon={null}
                    >
                        {availableMinutes.map((minute) => (
                            <Option key={minute} value={minute}>
                                <span className='text-center'>{minute}</span>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className="flex flex-col">
                    <label className="text-xs text-neutral-600 font-semibold mb-1 text-center">AM/PM</label>
                    <Select
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}
                        className="w-16 text-center"
                        size="small"
                        suffixIcon={null}
                    >
                        <Option value="AM"><span className='text-center'>AM</span></Option>
                        <Option value="PM"><span className='text-center'>PM</span></Option>
                    </Select>
                </div>
            </div>
        )
    }

    return (
        <div className='flex flex-col border border-solid border-neutral-300 bg-neutral-50 p-2 rounded-lg'>
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Select time for collection</h2>
            </div>
            {!isDeliveryDate && <>
                {store?.storemeta?.settings?.delivery_slots?.length > 0 ?
                    <div className="flex flex-col pt-2">
                        <div>
                            <span className="text-sm text-neutral-700 font-semibold text-center">{store?.storemeta?.settings?.delivery_slots_heading || "Select Slot"}</span>
                        </div>

                        <div className="flex flex-wrap gap-2 items-end py-2">
                            {(() => {
                                const filteredSlots = store?.storemeta?.settings?.delivery_slots?.filter((slot: string) => {
                                    const selectDate = moment(selectedDate);
                                    const today = moment();

                                    // If selected date is today, filter out past time slots
                                    if (selectDate?.isSame(today, 'day')) {
                                        return moment(slot, 'HH:mm').isAfter(moment());
                                    }

                                    // If selected date is in the future, show all slots
                                    return true;
                                });

                                // Check if no slots are available
                                if (!filteredSlots || filteredSlots.length === 0) {
                                    return (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-red-500 font-semibold">No slots available for Today.</span>
                                        </div>
                                    );
                                }

                                // Render the slots
                                return filteredSlots.map((slot: string) => {
                                    return (
                                        <button
                                            key={slot}
                                            onClick={() => handleTimeSelect(slot)}
                                            className={`flex items-center p-2 text-xs justify-center rounded-md cursor-pointer text-center ${selectedTime === slot ? 'bg-green-500 text-white' : 'bg-neutral-200 text-neutral-700'
                                                }`}
                                        >
                                            <span className="text-xs font-semibold leading-tight">
                                                {moment(slot, 'HH:mm').format('hh:mm A')}
                                            </span>
                                        </button>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                    :
                    timeSelection()}
            </>}
            {/* Delivery Date and Time Header */}
            {store?.storemeta?.settings?.schedule_for_later && <div className="flex items-center justify-start gap-2 py-2">
                <Switch
                    checked={isDeliveryDate}
                    onChange={() => setIsDeliveryDate(!isDeliveryDate)}
                    size='small'
                    className=''
                />
                <span className={`text-sm ${isDeliveryDate ? 'font-semibold text-success-500' : 'text-neutral-700'}`}>
                    Schedule for Later
                </span>
            </div>}

            {/* Date Selection */}
            {isDeliveryDate && <div className='flex flex-col'>
                <div className="flex items-center gap-2">
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        onClick={handlePrevWeek}
                        disabled={!canGoPrev}
                        className="flex items-center justify-center w-6 h-6 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                        size="small"
                    />

                    <div className="flex gap-1 flex-1 overflow-x-auto">
                        {dates?.map((date, index) => (
                            <div
                                key={index}
                                onClick={() => handleDateSelect(date)}
                                className={`
                                    flex flex-col items-center justify-center min-w-[32px] h-12 rounded-md cursor-pointer transition-all text-center
                                    ${selectedDate?.isSame(date, 'day')
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }
                                `}
                            >
                                <span className="text-xs font-medium leading-tight">
                                    {date.format('ddd')}
                                </span>
                                <span className="text-xs font-semibold leading-tight">
                                    {date.format('D')}
                                </span>
                            </div>
                        ))}
                    </div>

                    <Button
                        type="text"
                        icon={<RightOutlined />}
                        onClick={handleNextWeek}
                        disabled={!canGoNext}
                        className="flex items-center justify-center w-6 h-6 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                        size="small"
                    />
                </div>

                {/* Time Selection */}
                {store?.storemeta?.settings?.delivery_slots?.length > 0 ?
                    <div className="flex flex-col pt-2">
                        <div>
                            <span className="text-sm text-neutral-700 font-semibold text-center">{store?.storemeta?.settings?.delivery_slots_heading || "Select Slot"}</span>
                        </div>

                        <div className="flex flex-wrap gap-2 items-end py-2">
                            {(() => {
                                const filteredSlots = store?.storemeta?.settings?.delivery_slots?.filter((slot: string) => {
                                    const selectDate = moment(selectedDate);
                                    const today = moment();

                                    // If selected date is today, filter out past time slots
                                    if (selectDate?.isSame(today, 'day')) {
                                        return moment(slot, 'HH:mm').isAfter(moment());
                                    }

                                    // If selected date is in the future, show all slots
                                    return true;
                                });

                                // Check if no slots are available
                                if (!filteredSlots || filteredSlots.length === 0) {
                                    return (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-red-500 font-semibold">No slots available for selected date, please select another date</span>
                                        </div>
                                    );
                                }

                                // Render the slots
                                return filteredSlots.map((slot: string) => {
                                    return (
                                        <button
                                            key={slot}
                                            onClick={() => handleTimeSelect(slot)}
                                            className={`flex items-center p-2 text-xs justify-center rounded-md cursor-pointer text-center ${selectedTime === slot ? 'bg-green-500 text-white' : 'bg-neutral-200 text-neutral-700'
                                                }`}
                                        >
                                            <span className="text-xs font-semibold leading-tight">
                                                {moment(slot, 'HH:mm').format('hh:mm A')}
                                            </span>
                                        </button>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                    :
                    timeSelection()}
            </div>}
        </div>
    )
}

export default DeliverySlots